import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';

const BUSINESS_DETAILS = {
  address: "Lumiotec HQ, 3rd Floor, Moi Avenue, Nairobi, Kenya",
  phone: "+254 700 123 456",
  email: "support@lumiotec.co.ke",
  mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.823971271172!2d36.82023937517208!3d-1.2801450356199426!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d4814f3a7f%3A0x67a304499d0c365b!2sNairobi%20CBD!5e0!3m2!1sen!2ske!4v1700000000000!5m2!1sen!2ske"
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    document.title = "Lumiotec | Contact Us";
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('Sending message...');

    setTimeout(() => {
      console.log('Form Submitted:', formData);
      setIsSubmitting(false);
      setFormData({ name: '', email: '', message: '' });
      setStatus('Thank you! Your message has been sent successfully. We will respond within 24 hours.');
      
      setTimeout(() => setStatus(''), 5000);
    }, 2000);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
            Get in Touch with Lumiotec
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            We are here to assist you with sales, support, and technical inquiries.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 mt-16">
          
          <div className="bg-white p-8 rounded-lg shadow-md border w-full lg:w-1/3">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Contact Information</h3>
            <p className="text-gray-600 mb-8">
              Reach out to us directly through the channels below. We are open Monday to Friday, 9:00 AM to 5:00 PM (EAT).
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <MapPin size={24} className="flex-shrink-0 text-red-500 mt-1" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Visit Us</h4>
                  <p className="text-gray-600 mt-1">{BUSINESS_DETAILS.address}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone size={24} className="flex-shrink-0 text-red-500 mt-1" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Call Us</h4>
                  <p className="text-gray-600 mt-1">{BUSINESS_DETAILS.phone}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail size={24} className="flex-shrink-0 text-red-500 mt-1" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Email Us</h4>
                  <p className="text-gray-600 mt-1 truncate">{BUSINESS_DETAILS.email}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md border w-full lg:w-2/3">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
            
            {status && (
              <div className={`p-4 mb-6 rounded-lg font-medium ${status.includes('success') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                {status}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Message or Inquiry</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm resize-vertical focus:ring-red-500 focus:border-red-500"
                  placeholder="Tell us about your order, technical question, or partnership inquiry..."
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-400"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin-fast border-2 border-white border-t-transparent rounded-full w-5 h-5 mr-3"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-16">
            <h3 className="text-center text-4xl font-extrabold text-gray-900">Find Our Location</h3>
            <div className="mt-8 bg-white p-2 rounded-lg shadow-md border">
                <iframe 
                    src={BUSINESS_DETAILS.mapLink}
                    width="100%" 
                    height="450" 
                    className="border-0 rounded-md"
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Lumiotec Location Map"
                ></iframe>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
